import type { WorkflowRunListSortField } from '@erp/rpc';

export const RUN_STATUS_OPTIONS = [
  'scheduled',
  'queued',
  'running',
  'idle',
  'review',
  'completed',
  'failed',
  'cancelled',
  'terminated',
] as const;

export const RUN_STATUS_FILTER_OPTIONS = [
  'all',
  ...RUN_STATUS_OPTIONS,
] as const;

export const RUN_LIST_SORT_FIELDS = [
  'triggeredAt',
  'status',
  'version',
] as const satisfies readonly WorkflowRunListSortField[];

export type RunStatusOption = (typeof RUN_STATUS_OPTIONS)[number];
export type RunStatusFilter = (typeof RUN_STATUS_FILTER_OPTIONS)[number];
export type TriggerSourceFilter = 'all' | 'user' | 'system';
export type RunListSortDirection = 'asc' | 'desc';

export interface RunListSort {
  sortBy: WorkflowRunListSortField;
  sortDirection: RunListSortDirection;
}

export const DEFAULT_RUN_LIST_SORT: RunListSort = {
  sortBy: 'triggeredAt',
  sortDirection: 'desc',
};

export function isRunStatusOption(value: string): value is RunStatusOption {
  return RUN_STATUS_OPTIONS.some((status) => status === value);
}

export function isTriggerSourceFilter(
  value: string,
): value is TriggerSourceFilter {
  return value === 'all' || value === 'user' || value === 'system';
}

export function isRunListSortField(
  value: string | undefined,
): value is WorkflowRunListSortField {
  return RUN_LIST_SORT_FIELDS.some((field) => field === value);
}

function parseDateTimeInput(value: string): number | undefined {
  if (!value) {
    return undefined;
  }

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? undefined : timestamp;
}

function parseDateTimeInputEnd(value: string): number | undefined {
  const baseTimestamp = parseDateTimeInput(value);
  if (baseTimestamp === undefined) {
    return undefined;
  }

  // datetime-local commonly has minute precision, so make "to" inclusive.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
    return baseTimestamp + 59_999;
  }

  return baseTimestamp;
}

export interface TriggeredRangeQuery {
  hasInvalidDateRange: boolean;
  queryTriggeredFrom: string | undefined;
  queryTriggeredTo: string | undefined;
}

/** ISO bounds for the triggered-at range; both bounds drop when inverted. */
export function resolveTriggeredRange(
  triggeredFrom: string,
  triggeredTo: string,
): TriggeredRangeQuery {
  const fromTimestamp = parseDateTimeInput(triggeredFrom);
  const toTimestamp = parseDateTimeInputEnd(triggeredTo);
  const hasInvalidDateRange =
    fromTimestamp !== undefined &&
    toTimestamp !== undefined &&
    fromTimestamp > toTimestamp;
  const toIso = (timestamp: number | undefined) =>
    hasInvalidDateRange || timestamp === undefined
      ? undefined
      : new Date(timestamp).toISOString();

  return {
    hasInvalidDateRange,
    queryTriggeredFrom: toIso(fromTimestamp),
    queryTriggeredTo: toIso(toTimestamp),
  };
}

export interface StatusQuery {
  /** Single-value view of the selection for the All Runs page. */
  statusFilter: RunStatusFilter;
  queryStatus: RunStatusOption | undefined;
  queryStatuses: RunStatusOption[] | undefined;
  queryOnlyScheduledReruns: true | undefined;
}

/**
 * The "scheduled" filter on its own is a pseudo-filter: the backend handles
 * it via onlyScheduledReruns (future scheduled runs, sorted soonest-first),
 * not a plain status match — so don't also send it as a status. Combined
 * with other statuses it is a plain status match again.
 */
export function resolveStatusQuery(
  statuses: readonly RunStatusOption[],
): StatusQuery {
  const soleStatus = statuses.length === 1 ? statuses[0] : undefined;
  const onlyScheduledReruns = soleStatus === 'scheduled';
  return {
    statusFilter: soleStatus ?? 'all',
    queryStatus: onlyScheduledReruns ? undefined : soleStatus,
    queryStatuses:
      statuses.length === 0 || onlyScheduledReruns ? undefined : [...statuses],
    queryOnlyScheduledReruns: onlyScheduledReruns ? true : undefined,
  };
}

export interface SortQuery {
  isDefaultSort: boolean;
  querySortBy: WorkflowRunListSortField | undefined;
  querySortDirection: RunListSortDirection | undefined;
}

/** The server default sort is left implicit so its cache key stays stable. */
export function resolveSortQuery(sort: RunListSort): SortQuery {
  const isDefaultSort =
    sort.sortBy === DEFAULT_RUN_LIST_SORT.sortBy &&
    sort.sortDirection === DEFAULT_RUN_LIST_SORT.sortDirection;
  return {
    isDefaultSort,
    querySortBy: isDefaultSort ? undefined : sort.sortBy,
    querySortDirection: isDefaultSort ? undefined : sort.sortDirection,
  };
}

/** Normalizes a sort request; anything unknown falls back to the default. */
export function resolveRequestedSort(
  sortBy: string | undefined,
  sortDirection: RunListSortDirection | undefined,
): RunListSort {
  if (isRunListSortField(sortBy) && sortDirection) {
    return { sortBy, sortDirection };
  }
  return DEFAULT_RUN_LIST_SORT;
}

export function normalizeSearchTerm(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

export function hasActiveRunFilters({
  statuses,
  triggerSourceFilter,
  searchTerm,
  triggeredFrom,
  triggeredTo,
  testCaseIds,
}: {
  statuses: readonly string[];
  triggerSourceFilter: TriggerSourceFilter;
  searchTerm: string;
  triggeredFrom: string;
  triggeredTo: string;
  testCaseIds: readonly string[];
}): boolean {
  return (
    statuses.length > 0 ||
    triggerSourceFilter !== 'all' ||
    normalizeSearchTerm(searchTerm) !== undefined ||
    triggeredFrom.length > 0 ||
    triggeredTo.length > 0 ||
    testCaseIds.length > 0
  );
}
